import { useState } from 'react';

import clsx from 'clsx';

import CheckIcon from '../../assets/CheckIcon';
import ChevronDownIcon from '../../assets/ChevronDownIcon';
import useStringText from '../../hooks/useStringText';
import { SurfaceLevel } from '../../types';
import InputLabel from './InputLabel';

interface OptionValue {
  key: string;
  value: string;
}

interface CommonProps {
  label: string;
  name: string;
  options: OptionValue[];
  isRequired?: boolean;
  surfaceLevel?: SurfaceLevel;
}

interface MultiSelectInputProps extends CommonProps {
  allowMultiple?: true;
  onChange?: (name: string, values: string[]) => void;
  value?: string[];
}

interface SingleSelectInputProps extends CommonProps {
  allowMultiple?: false;
  className?: string;
  disabled?: boolean;
  isLabelOutside?: boolean;
  onChange?: (name: string, value: string) => void;
  value?: string;
}

export type SelectInputProps = { allowMultiple: boolean } & (MultiSelectInputProps | SingleSelectInputProps);

const isValidValue = (value: string, options: OptionValue[]) => options.some((option) => option.value === value);

const SingleSelectInput = ({
  className,
  disabled = false,
  isLabelOutside = false,
  isRequired = false,
  label,
  name,
  onChange,
  options,
  surfaceLevel = SurfaceLevel.L0,
  value,
}: SingleSelectInputProps) => {
  const pleaseSelectText = useStringText('please_select');
  const isControlled = value !== undefined;

  // Make sure it's a valid value
  const selectedValue = isControlled && isValidValue(value, options) ? value : null;

  const [internalValue, setInternalValue] = useState<null | string>(selectedValue);
  const id = name;

  const backgroundClassName = surfaceLevel === SurfaceLevel.L0 ? 'bg-surface-l1' : 'bg-surface-l2';

  return (
    <div className={clsx('flex flex-col gap-2xs', className)}>
      {isLabelOutside && (
        <InputLabel
          className="!text-text-secondary"
          id={id}
          isRequired={isRequired}
          label={label}
        />
      )}
      <div className="relative w-full">
        <select
          className={clsx(
            'w-full outline-none appearance-none cursor-pointer border rounded-sm',
            isLabelOutside ? 'p-sm' : 'pt-lg pb-2xs px-sm',
            surfaceLevel === SurfaceLevel.L0 ? 'border-border-l0' : 'border-border-l1',
            disabled ? 'bg-plum-800' : backgroundClassName,
            disabled && 'cursor-not-allowed',
            !disabled && 'can-hover:hover:border-border-active focus:border-border-active',
            'placeholder:text-text-primary-inactive text-text-primary'
          )}
          disabled={disabled}
          onChange={(e) => {
            const selected = options.find((option) => option.value === e.target.value);
            const newValue = selected?.value ?? null;

            setInternalValue(newValue);
            onChange?.(name, newValue ?? '');
          }}
          value={internalValue ?? ''}
        >
          <option
            className="hidden"
            disabled
            selected={internalValue === null}
            value=""
          />
          {options.map(({ key, value }) => {
            return (
              <option
                key={value}
                selected={internalValue === value}
                value={value}
              >
                {key}
              </option>
            );
          })}
        </select>
        <input
          checked={internalValue !== null}
          className="hidden peer"
          name={name}
          type="checkbox"
        />
        {(!isLabelOutside || internalValue === null) && (
          <InputLabel
            className={clsx(
              'transition-input absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none',
              'peer-checked:top-2 peer-checked:translate-y-0 peer-checked:text-caption'
            )}
            id={id}
            isRequired={isRequired}
            label={isLabelOutside ? pleaseSelectText : label}
          />
        )}
        <div className="absolute pointer-events-none top-1/2 -translate-y-1/2 right-4 w-8 h-8 fill-icon-inactive">
          <ChevronDownIcon className="w-8 h-8" />
        </div>
      </div>
    </div>
  );
};

const MultiSelectInput = ({
  isRequired, label, name, onChange, options, surfaceLevel, value,
}: MultiSelectInputProps) => {
  // Make sure it's a valid value
  const selectedValues = value?.filter((value) => isValidValue(value, options)) ?? [];

  const [internalValues, setInternalValues] = useState<Set<string>>(new Set(selectedValues));

  const currentValues = new Set(selectedValues ?? internalValues);

  const handleChange = ({ isChecked, value }: {
    isChecked: boolean,
    value: string
  }) => {
    const newSet = new Set(currentValues);

    if (isChecked) {
      newSet.add(value);
    } else {
      newSet.delete(value);
    }

    setInternalValues(newSet);
    onChange?.(name, [...newSet]);
  };

  return (
    <div className="flex flex-col gap-2xs">
      <InputLabel
        className="!text-text-secondary"
        id={name}
        isRequired={isRequired}
        label={label}
      />
      <div
        className={clsx(
          'p-sm flex flex-col gap-2xs rounded-sm border',
          surfaceLevel === SurfaceLevel.L0 ? 'bg-surface-l1' : 'bg-surface-l2',
          surfaceLevel === SurfaceLevel.L0 ? 'border-border-l0' : 'border-border-l1'
        )}
      >
        {options.map(({ key, value }, idx) => {
          const id = `${name}-${idx}`;
          const isChecked = currentValues.has(value);

          return (
            <div key={value} className="group flex items-center gap-2xs">
              <input
                checked={isChecked}
                className="hidden peer"
                id={id}
                onChange={(e) => {
                  handleChange({
                    isChecked: e.target.checked,
                    value,
                  });
                }}
                type="checkbox"
                value={value}
              />
              <span
                className={clsx(
                  'w-4 h-4 p-[1.5px]',
                  'border border-icon-inactive rounded-sm fill-transparent',
                  'peer-checked:bg-text-primary peer-checked:fill-surface-l1'
                )}
                onClick={() => {
                  handleChange({
                    isChecked: !isChecked,
                    value,
                  });
                }}
              >
                <CheckIcon />
              </span>
              <label
                className={clsx(
                  'text-p2 cursor-pointer',
                  'text-text-primary-inactive peer-checked:text-text-primary peer-checked:font-medium'
                )}
                htmlFor={id}
              >
                {key}
              </label>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const SelectInput = ({
  allowMultiple,
  label,
  name,
  onChange,
  options,
  surfaceLevel,
  value,
  ...props
}: SelectInputProps) => {
  if (allowMultiple) {
    return (
      <MultiSelectInput
        label={label}
        name={name}
        onChange={onChange}
        options={options}
        surfaceLevel={surfaceLevel}
        value={value}
      />
    );
  }

  return (
    <SingleSelectInput
      label={label}
      name={name}
      onChange={onChange}
      options={options}
      surfaceLevel={surfaceLevel}
      value={value}
      {...props}
    />
  );
};

export default SelectInput;
